import { z } from 'zod';

export const CommunicationAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  contentType: z.string(),
});

export type CommunicationAttachment = z.infer<
  typeof CommunicationAttachmentSchema
>;

export const CommunicationParticipantSchema = z.object({
  address: z.string().optional(),
  name: z.string().optional(),
});
export type CommunicationParticipant = z.infer<
  typeof CommunicationParticipantSchema
>;

export const SiteSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type Site = z.infer<typeof SiteSchema>;

export const BaseCommunicationSchema = z.object({
  id: z.string(),
  date: z.date(),
});

export const EmailCommunicationSchema = BaseCommunicationSchema.merge(
  z.object({
    type: z.literal('email'),
    subject: z.string().default(''),
    body: z.string().default(''),
    sender: CommunicationParticipantSchema,
    recipients: z.array(CommunicationParticipantSchema).default([]),
    cc: z.array(CommunicationParticipantSchema).default([]),
    attachments: z.array(CommunicationAttachmentSchema).default([]),
    linkedSites: z.array(SiteSchema).default([]),
  }),
);

export type EmailCommunication = z.infer<typeof EmailCommunicationSchema>;

export const MeetingMinutesCommunicationSchema = BaseCommunicationSchema.merge(
  z.object({
    type: z.literal('meeting-minutes'),
    content: z.string(),
    title: z.string().optional(),
    meeting: z
      .object({ id: z.string(), title: z.string().optional() })
      .optional(),
  }),
);

export type MeetingMinutesCommunication = z.infer<
  typeof MeetingMinutesCommunicationSchema
>;

export const MeetingCommunicationSchema = BaseCommunicationSchema.merge(
  z.object({
    type: z.literal('meeting'),
    title: z.string(),
    start: z.date().optional(),
    end: z.date().optional(),
    location: z.string().optional(),
    attendees: z.array(CommunicationParticipantSchema).default([]),
    summary: z.string().default(''),
    attachments: z.array(CommunicationAttachmentSchema).default([]),
    meetingMinutes: z
      .array(
        z.object({
          id: z.string(),
          title: z.string().optional(),
          content: z.string(),
        }),
      )
      .default([]),
    linkedSites: z.array(SiteSchema).default([]),
  }),
);

export type MeetingCommunication = z.infer<typeof MeetingCommunicationSchema>;

export const CommunicationSchema = z.discriminatedUnion('type', [
  EmailCommunicationSchema,
  MeetingCommunicationSchema,
]);

export type Communication = z.infer<typeof CommunicationSchema>;

export const CommunicationPaginationSchema = z.object({
  totalCount: z.number(),
  nextCursor: z.number().optional(),
  communications: z.array(CommunicationSchema),
});
export type CommunicationPagination = z.infer<
  typeof CommunicationPaginationSchema
>;
